import type {
	HreflangEntry,
	PageIssue,
	ProposedFix,
	TechnicalAuditFixPreview,
} from "@/types/technicalAudit";
import { readHreflangPreview } from "@/types/technicalAudit";

// Shared per-issue-type Before/After derivation. Extracted verbatim from
// ApplyFixPreviewDialog.tsx so both the apply-preview dialog AND the
// verify-result modal render the same diff. Behavior-preserving: the apply
// preview passes a fetched `preview` (planned_payload); the verify modal calls
// the `buildIssueBeforeAfter` wrapper, which omits it (preview = null).

// Some crawls store a MALFORMED broken-link URL when the source anchor markup is
// itself broken, e.g. `https://site.com/">About Us</a></li> <li><a href=`. The
// backend already sanitizes this in the suggestion/apply paths; this mirrors that
// same regex for DISPLAY-ONLY use (never for values submitted to apply). It cuts
// the string at the first markup marker — a literal or %-encoded quote / angle
// bracket / smart quote — while preserving legit `%20`/clean URLs.
export function sanitizeBrokenUrl<T extends string | null | undefined>(url: T): T {
	if (typeof url !== "string" || !url) return url;
	const cleaned = String(url).trim().split(/["<>“”]|%22|%3[CcEe]|%E2%80%9[CcDd]/)[0];
	return (cleaned || url) as T;
}

// Render one hreflang alternate the way it appears in the page head, so the
// hreflang Before/After reads as the actual tag set being rewritten.
export const formatHreflangLinkTag = (entry: HreflangEntry): string =>
	`<link rel="alternate" hreflang="${entry.hreflang}" href="${entry.href}">`;

// The "After" marker when the staged hreflang plan removes every tag
// (mode "suppress": bogus hreflang on a single-language site).
export const NO_HREFLANG_TAGS = "(no hreflang tags)";

export const stringifyValue = (value: unknown): string => {
	if (value === null || value === undefined || value === "") return "Not available";
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean") return String(value);
	return JSON.stringify(value, null, 2);
};

// thin_content append is a no-op unless at least one recommended section has a
// non-empty draft OR at least one draft addition is non-empty (the backend skips
// heading-only sections and bails on an empty append). Drives the apply gate.
export function hasApplicableExpansion(packet: Record<string, unknown> | null): boolean {
	if (!packet) return false;
	const sections = Array.isArray(packet.recommended_sections)
		? (packet.recommended_sections as Array<Record<string, unknown>>)
		: [];
	const hasDraftSection = sections.some(
		(s) => typeof s?.draft === "string" && s.draft.trim().length > 0,
	);
	const additions = Array.isArray(packet.draft_additions)
		? (packet.draft_additions as unknown[])
		: [];
	const hasAddition = additions.some(
		(a) => typeof a === "string" && a.trim().length > 0,
	);
	return hasDraftSection || hasAddition;
}

// Extract the destination URL from a fix value (edited or stored), across the
// resolver shapes: replacement_url / redirect_to / recommended_target / link_edits[].to_url.
function destinationFrom(value: unknown): string | undefined {
	if (!value) return undefined;
	let obj: Record<string, unknown> | null = null;
	if (typeof value === "string") {
		try {
			obj = JSON.parse(value);
		} catch {
			return undefined;
		}
	} else if (typeof value === "object") {
		obj = value as Record<string, unknown>;
	}
	if (!obj) return undefined;
	const o = obj as Record<string, unknown> & {
		link_edits?: Array<{ to_url?: string }>;
	};
	if (typeof o.replacement_url === "string" && o.replacement_url) return o.replacement_url;
	if (typeof o.redirect_to === "string" && o.redirect_to) return o.redirect_to;
	if (typeof o.recommended_target === "string" && o.recommended_target)
		return o.recommended_target;
	if (Array.isArray(o.link_edits)) {
		const tos = o.link_edits.map((e) => e?.to_url).filter(Boolean) as string[];
		if (tos.length) return tos.join(", ");
	}
	return undefined;
}

// Extract the proposed SEO title / meta description from a fix value (edited or
// stored), tolerating both the parsed object and a JSON string.
function seoMetaFrom(
	value: unknown,
): {
	new_seo_title?: string;
	new_title?: string;
	new_meta_description?: string;
} | undefined {
	if (!value) return undefined;
	let obj: Record<string, unknown> | null = null;
	if (typeof value === "string") {
		try {
			obj = JSON.parse(value);
		} catch {
			return undefined;
		}
	} else if (typeof value === "object") {
		obj = value as Record<string, unknown>;
	}
	if (!obj) return undefined;
	// Prefer the canonical SEO/meta title key; fall back to legacy `new_title`.
	const title =
		typeof obj.new_seo_title === "string"
			? obj.new_seo_title
			: typeof obj.new_title === "string"
				? obj.new_title
				: undefined;
	const meta =
		typeof obj.new_meta_description === "string"
			? obj.new_meta_description
			: undefined;
	if (!title && !meta) return undefined;
	return { new_seo_title: title, new_title: title, new_meta_description: meta };
}

// Parse an orphan-page fix value (edited or stored) into an object, tolerating
// both the parsed object and a JSON string.
export function orphanFixObject(value: unknown): Record<string, unknown> | null {
	if (!value) return null;
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value);
			return parsed && typeof parsed === "object" && !Array.isArray(parsed)
				? (parsed as Record<string, unknown>)
				: null;
		} catch {
			return null;
		}
	}
	if (typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return null;
}

// Extract the orphan-page internal-link fix from a value (edited or stored),
// tolerating both the parsed object and a JSON string.
function orphanLinkFrom(
	value: unknown,
): { source_page_url?: string; anchor_text?: string } | undefined {
	const obj = orphanFixObject(value);
	if (!obj) return undefined;
	const source =
		typeof obj.source_page_url === "string" ? obj.source_page_url : undefined;
	const anchor =
		typeof obj.anchor_text === "string" ? obj.anchor_text : undefined;
	if (!source && !anchor) return undefined;
	return { source_page_url: source, anchor_text: anchor };
}

type SelectedSource = {
	source_page_url: string;
	anchor_text: string;
	title?: string;
	snippet?: string | null;
	insertion_mode?: string;
	original_sentence?: string;
	source_sentence?: string;
	rewritten_sentence?: string;
};

// The inbound link is spread across several blogs — read the selected list from
// `source_pages` (edited or stored). Falls back to the legacy single
// {source_page_url, anchor_text} shape when no list is present.
function selectedSourcesFrom(value: unknown): SelectedSource[] {
	const obj = orphanFixObject(value);
	if (!obj) return [];
	const list = Array.isArray(obj.source_pages)
		? (obj.source_pages as Array<Record<string, unknown>>)
		: [];
	const fallbackAnchor =
		typeof obj.anchor_text === "string" ? obj.anchor_text : "";
	const out: SelectedSource[] = [];
	for (const p of list) {
		const url =
			typeof p?.source_page_url === "string" ? p.source_page_url : undefined;
		if (!url) continue;
		out.push({
			source_page_url: url,
			anchor_text:
				typeof p.anchor_text === "string" && p.anchor_text
					? p.anchor_text
					: fallbackAnchor,
			title: typeof p.title === "string" ? p.title : undefined,
			snippet: typeof p.snippet === "string" ? p.snippet : null,
			insertion_mode:
				typeof p.insertion_mode === "string" ? p.insertion_mode : undefined,
			original_sentence:
				typeof p.original_sentence === "string"
					? p.original_sentence
					: typeof p.source_sentence === "string"
						? p.source_sentence
						: undefined,
			source_sentence:
				typeof p.source_sentence === "string" ? p.source_sentence : undefined,
			rewritten_sentence:
				typeof p.rewritten_sentence === "string"
					? p.rewritten_sentence
					: undefined,
		});
	}
	return out;
}

// Look up the recommended source the specialist selected (by source_page_url)
// from the stored orphan/low_internal_links fix, falling back to the top
// recommendation. Returns its anchor-bearing section snippet and post type so
// the "After" side can show where the new link lands.
function selectedSourceSnippet(
	storedFix: Record<string, unknown> | null,
	sourceUrl?: string,
): { snippet: string | null; post_type?: string } | null {
	if (!storedFix || !Array.isArray(storedFix.recommended_source_pages)) return null;
	const sources = storedFix.recommended_source_pages as Array<{
		url?: string;
		snippet?: string | null;
		post_type?: string;
	}>;
	if (sources.length === 0) return null;
	const match =
		(sourceUrl && sources.find((s) => s?.url === sourceUrl)) || sources[0];
	if (!match) return null;
	return {
		snippet: typeof match.snippet === "string" ? match.snippet : null,
		post_type: typeof match.post_type === "string" ? match.post_type : undefined,
	};
}

// Render a snippet with the anchor phrase emphasized via a simple "*anchor*"
// marker (the Before/After panes render as plain <pre>, so we mark the anchor
// with asterisks rather than bold markup).
function emphasizeAnchor(snippet: string, anchor: string): string {
	const trimmed = anchor.trim();
	if (!trimmed) return snippet;
	const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	return snippet.replace(new RegExp(`(${escaped})`, "gi"), "*$1*");
}

// Extract the low-readability rewrite fix from a value (edited or stored),
// tolerating both the parsed object and a JSON string (mirrors orphanLinkFrom).
// The edited value carries only `rewritten_body`; the stored fix also carries
// the before/after scores and block counts used for the Before/After preview.
function readabilityFrom(value: unknown):
	| {
			rewritten_body?: string;
			readability_score_before?: number;
			readability_score_after?: number;
			grade_before?: string;
			grade_after?: string;
			blocks_rewritten?: number;
			blocks_total?: number;
			sentence_count?: number;
	  }
	| undefined {
	const obj = orphanFixObject(value);
	if (!obj) return undefined;
	const str = (k: string) => (typeof obj[k] === "string" ? (obj[k] as string) : undefined);
	const num = (k: string) => (typeof obj[k] === "number" ? (obj[k] as number) : undefined);
	const result = {
		rewritten_body: str("rewritten_body"),
		readability_score_before: num("readability_score_before"),
		readability_score_after: num("readability_score_after"),
		grade_before: str("grade_before"),
		grade_after: str("grade_after"),
		blocks_rewritten: num("blocks_rewritten"),
		blocks_total: num("blocks_total"),
		sentence_count: Array.isArray(obj.sentence_diffs)
			? (obj.sentence_diffs as unknown[]).length
			: undefined,
	};
	if (Object.values(result).every((v) => v === undefined)) return undefined;
	return result;
}

// The current (broken) link/URL being changed, for the "Before" side.
function sourceFrom(issue: PageIssue): string {
	const d = (issue.issue_details || {}) as Record<string, unknown>;
	let stored: { link_edits?: Array<{ from_url?: string }>; dead_url?: string } | null = null;
	const raw = issue.approved_value ?? issue.proposed_value;
	if (raw && typeof raw === "object") stored = raw as typeof stored;
	else if (typeof raw === "string") {
		try {
			stored = JSON.parse(raw);
		} catch {
			stored = null;
		}
	}
	const fromLinks = Array.isArray(stored?.link_edits)
		? (stored?.link_edits.map((e) => e?.from_url).filter(Boolean) as string[]).join(", ")
		: "";
	return (
		(d.broken_url as string) ||
		(d.target_url as string) ||
		fromLinks ||
		stored?.dead_url ||
		(d.source_url as string) ||
		issue.page_url ||
		"Not available"
	);
}

// Parse the crawl's intrinsic_dimensions string ("1600 x 900", "1600x900",
// "1600 × 900") into numeric width/height. Screaming Frog exports the
// "Dimensions" column in the "W x H" shape; anything unparseable returns null.
export function parseIntrinsicDimensions(
	value: unknown,
): { width: number; height: number } | null {
	if (typeof value !== "string") return null;
	const match = value.match(/(\d+)\s*[x×]\s*(\d+)/i);
	if (!match) return null;
	const width = Number.parseInt(match[1], 10);
	const height = Number.parseInt(match[2], 10);
	if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0)
		return null;
	return { width, height };
}

// Backend-supplied img tag pair (preview enrichment / apply result):
// { before_tag: '<img src="…">', after_tag: '<img src="…" width="…" height="…">' }
// — the backend's apply result and its apply_preview object also mirror the
// same strings under plain { before, after } keys, so accept either pair.
// Tolerates an object or a JSON string; returns null when either tag is absent.
export function imgTagsFrom(
	value: unknown,
): { before_tag: string; after_tag: string } | null {
	const obj = orphanFixObject(value);
	if (!obj) return null;
	const str = (k: string) => (typeof obj[k] === "string" ? (obj[k] as string).trim() : "");
	const before = str("before_tag") || str("before");
	const after = str("after_tag") || str("after");
	if (!before || !after) return null;
	return { before_tag: before, after_tag: after };
}

// Derive the display-only before/after <img> markup for an
// image_missing_dimensions issue from its crawl details. The "before" tag keeps
// any dimension attribute the crawl saw as present (only the listed
// missing_attributes are absent); the "after" tag carries both width and height
// from the crawl-measured intrinsic dimensions, or W/H placeholders when the
// crawl couldn't measure them (the backend then resolves them from the media
// library / a remote probe at apply time).
export function buildImgDimensionsTagPreview(details: Record<string, unknown>): {
	before: string;
	after: string;
	width: string;
	height: string;
	hasDimensions: boolean;
} {
	const src = typeof details.image_url === "string" && details.image_url
		? details.image_url
		: "image.jpg";
	const missing = Array.isArray(details.missing_attributes)
		? (details.missing_attributes as unknown[]).filter(
				(a): a is string => typeof a === "string",
			)
		: ["width", "height"];
	const dims = parseIntrinsicDimensions(details.intrinsic_dimensions);
	const width = dims ? String(dims.width) : "W";
	const height = dims ? String(dims.height) : "H";
	const beforeAttrs = [`src="${src}"`];
	if (!missing.includes("width")) beforeAttrs.push(`width="${width}"`);
	if (!missing.includes("height")) beforeAttrs.push(`height="${height}"`);
	return {
		before: `<img ${beforeAttrs.join(" ")}>`,
		after: `<img src="${src}" width="${width}" height="${height}">`,
		width,
		height,
		hasDimensions: Boolean(dims),
	};
}

export function buildBeforeAfter(
	issue: PageIssue | null,
	preview: TechnicalAuditFixPreview | null,
	editedValue?: ProposedFix,
) {
	if (!issue) return { before: "Not available", after: "Not available" };

	const details = issue.issue_details || {};
	const planned = preview?.planned_payload || {};
	const approved = issue.approved_value || issue.proposed_fix || issue.proposed_value;
	const emptyAfter = (fallback: string) =>
		planned.suggestion_status === "unavailable"
			? "No suggestion could be generated. Edit a value before applying."
			: fallback;

	switch (issue.issue_type) {
		case "title_meta":
		case "title_too_short":
		case "duplicate_title":
		case "serp_title_truncated":
		// Atomic TITLE-ONLY types (wantTitle, never wantMeta).
		case "page_title_same_as_h1":
		case "page_title_missing":
		case "page_title_over_60_chars":
		case "page_title_below_30_chars":
		case "duplicate_meta":
		case "serp_meta_truncated":
		// Atomic META-ONLY types (wantMeta, never wantTitle).
		case "meta_missing":
		case "meta_description_over_155_chars":
		case "meta_description_below_70_chars": {
			const d = details as Record<string, unknown>;
			const wantMeta =
				issue.issue_type === "title_meta" ||
				issue.issue_type === "duplicate_meta" ||
				issue.issue_type === "serp_meta_truncated" ||
				issue.issue_type === "meta_missing" ||
				issue.issue_type === "meta_description_over_155_chars" ||
				issue.issue_type === "meta_description_below_70_chars";
			const wantTitle =
				issue.issue_type !== "duplicate_meta" &&
				issue.issue_type !== "serp_meta_truncated" &&
				issue.issue_type !== "meta_missing" &&
				issue.issue_type !== "meta_description_over_155_chars" &&
				issue.issue_type !== "meta_description_below_70_chars";
			const proposed = seoMetaFrom(editedValue) ?? seoMetaFrom(approved);
			const currentTitle =
				(d.current_title as string) || (d.title as string) || "(empty)";
			const currentMeta =
				(d.current_meta_description as string) ||
				(d.current_meta as string) ||
				(d.description as string) ||
				"(empty)";
			const afterTitle =
				proposed?.new_seo_title ||
				proposed?.new_title ||
				emptyAfter("Generate or edit a SEO title tag before applying");
			const afterMeta =
				proposed?.new_meta_description ||
				emptyAfter("Generate or edit a description before applying");
			if (wantTitle && wantMeta) {
				return {
					before: `Title: ${currentTitle}\nMeta description: ${currentMeta}`,
					after: `Title: ${afterTitle}\nMeta description: ${afterMeta}`,
				};
			}
			return wantTitle
				? { before: currentTitle, after: afterTitle }
				: { before: currentMeta, after: afterMeta };
		}
		case "canonical": {
			const d = details as Record<string, unknown>;
			const current = (d.canonical_url as string) || (d.canonical_1 as string);
			const recommended = (d.recommended_canonical as string) || issue.page_url;
			// `before` is the current canonical, or a clear "none" marker when absent.
			const before = current || "(none — page has no canonical tag)";
			// A canonical on a different host than the page is treated as review-only:
			// cross-domain canonicals are usually intentional (e.g. syndication).
			const hostOf = (url?: string | null): string => {
				if (!url) return "";
				try {
					return new URL(url).host.replace(/^www\./, "");
				} catch {
					return "";
				}
			};
			const currentHost = hostOf(current);
			const pageHost = hostOf(issue.page_url);
			const after =
				currentHost && pageHost && currentHost !== pageHost
					? "Review only — canonical points to another domain (likely intentional). Not auto-applied unless overridden."
					: recommended;
			return { before: stringifyValue(before), after: stringifyValue(after) };
		}
		case "h1":
		case "h1_missing":
		case "h1_multiple":
		case "h1_too_long": {
			const currentH1s = details.current_h1s;
			const h1Before = Array.isArray(currentH1s)
				? currentH1s.filter((h) => typeof h === "string" && h.trim()).join("\n") || "No H1 found"
				: stringifyValue(details.current_h1 || "No H1 found");
			// A staged promote-selection is the ACTUAL planned change — previewing
			// the generated heading instead misrepresents what Apply will do.
			const promote = (
				editedValue as { promote_existing?: { text?: string; level?: string } } | undefined
			)?.promote_existing;
			if (promote?.text) {
				return {
					before: h1Before,
					after: `Promote existing ${promote.level || "heading"} to H1: "${promote.text}" (in place — nothing inserted)`,
				};
			}
			const h1Fix =
				orphanFixObject(editedValue) ??
				orphanFixObject(approved) ??
				orphanFixObject(planned.approved_value);
			const h1Text = (value: unknown): string =>
				typeof value === "string" && value.trim() ? value.trim() : "";
			const newH1 = h1Text(h1Fix?.keep_h1) || h1Text(h1Fix?.new_h1);
			return {
				before: h1Before,
				after:
					newH1 ||
					emptyAfter(h1Text(planned.recommendation) || "Generate or edit an H1 before applying"),
			};
		}
		case "duplicate_h1": {
			const d = details as Record<string, unknown>;
			const dupFix =
				orphanFixObject(editedValue) ??
				orphanFixObject(approved) ??
				orphanFixObject(planned.approved_value);
			const currentH1 = typeof d.h1_text === "string" ? d.h1_text.trim() : "";
			const newH1 = typeof dupFix?.new_h1 === "string" ? dupFix.new_h1.trim() : "";
			return {
				before: currentH1 || "No H1 recorded",
				after: newH1 || emptyAfter("Generate or edit an H1 before applying"),
			};
		}
			case "large_image": {
				const d = details as Record<string, unknown>;
				const p = planned as Record<string, unknown>;
				const kb = (n: number) => (n ? `${Math.round(n / 1024)} KB` : "unknown");
				let after =
					"Blog image: recompressed + resized in place (same URL). Landing/service-page images are protected and refused.";
				if (p.protected === true || p.preview_state === "protected") {
					after = "Protected: no WordPress image file will be changed.";
				} else if (p.requires_live_verification === true || p.preview_state === "candidate") {
					after =
						"Candidate blog image: verify the WordPress media parent first; only then recompress in place.";
				}
				return {
					before: stringifyValue({
						image: d.image_url,
						size: kb(Number(d.file_size_bytes || 0)),
						max: kb(Number(d.max_size_bytes || 0)),
					}),
					after: stringifyValue(after),
				};
			}
		case "alt_text": {
			const altFix =
				orphanFixObject(editedValue) ??
				orphanFixObject(approved) ??
				orphanFixObject(planned.approved_value);
			const newAlt =
				typeof altFix?.new_alt_text === "string" ? altFix.new_alt_text.trim() : "";
			const altBefore = stringifyValue(
				details.current_alt_text || details.alt_text || "(empty — no alt text)",
			);
			const altAfter =
				newAlt ||
				(typeof planned.recommendation === "string" && planned.recommendation) ||
				"Generate or edit alt text before applying";
			const imageUrl = (details as Record<string, unknown>).image_url;
			if (typeof imageUrl !== "string" || !imageUrl) {
				return { before: altBefore, after: altAfter };
			}
			return {
				before: `Image: ${imageUrl}\nAlt text: ${altBefore}`,
				after: `Image: ${imageUrl}\nAlt text: ${altAfter}`,
			};
		}
		case "image_missing_dimensions": {
			// Deterministic content edit: Apply inserts explicit width/height
			// attributes on the flagged <img> in the post content. Prefer the
			// backend's actual planned/applied tags (preview enrichment or apply
			// result) when present; otherwise derive the tag shapes from the crawl
			// details (intrinsic_dimensions is measured by the crawl, so it's
			// usually available — placeholders when it isn't).
			const p = planned as Record<string, unknown>;
			const backendTags =
				imgTagsFrom(p) ??
				imgTagsFrom(p.apply_preview) ??
				imgTagsFrom(approved);
			if (backendTags) {
				return {
					before: stringifyValue(backendTags.before_tag),
					after: stringifyValue(backendTags.after_tag),
				};
			}
			const derived = buildImgDimensionsTagPreview(
				details as Record<string, unknown>,
			);
			const after = derived.hasDimensions
				? derived.after
				: `${derived.after}\n\n(width/height are measured from the image file at apply time)`;
			return { before: stringifyValue(derived.before), after: stringifyValue(after) };
		}
		case "orphan_page":
		case "low_internal_links": {
			const d = details as Record<string, unknown>;
			// editedValue carries the selected `source_pages` list + anchor; the
			// target's own URL, the recommended sources, and their section snippets
			// come from the stored fix (or the page details). The kept
			// `orphan_page_url` key holds the under-linked target page for both types.
			const storedFix = orphanFixObject(approved);
			const orphanUrl =
				(typeof storedFix?.orphan_page_url === "string" && storedFix.orphan_page_url) ||
				(d.page_url as string) ||
				issue.page_url;
			// Prefer the multi-source selection; fall back to the legacy single link.
			let selected = selectedSourcesFrom(editedValue);
			if (selected.length === 0) selected = selectedSourcesFrom(approved);
			const legacy = orphanLinkFrom(editedValue) ?? orphanLinkFrom(approved);
			if (selected.length === 0 && (legacy?.source_page_url || legacy?.anchor_text)) {
				selected = [
					{
						source_page_url: legacy.source_page_url || "(pick a source page)",
						anchor_text: legacy.anchor_text || "",
					},
				];
			}
			// orphan_page reports inbound count as `incoming_links`;
			// low_internal_links reports it as `internal_link_count`.
			const inbound = Number(d.incoming_links ?? d.internal_link_count ?? 0);

			// LEFT / Before = the under-linked TARGET page + its inbound link count.
			const before = `Target page: ${orphanUrl}\n${inbound} inbound internal link${inbound === 1 ? "" : "s"}`;

			// RIGHT / After = one new inbound link PER selected source, each showing the
			// section snippet it lands in (anchor emphasized) → the target page.
			let after: string;
			if (selected.length > 0) {
				const lines = [
					`Link to: ${orphanUrl}`,
				];
				selected.forEach((s, i) => {
					const anchor = s.anchor_text || "";
					// Use the source's own snippet; fall back to the stored recommendation.
					const snippet =
						s.snippet ?? selectedSourceSnippet(storedFix, s.source_page_url)?.snippet ?? null;
					lines.push("");
					lines.push(`${i + 1}. ${s.title || s.source_page_url}`);
					if (s.title) lines.push(`   ${s.source_page_url}`);
					if (anchor) lines.push(`   Anchor: ${anchor}`);
					if (
						s.insertion_mode === "sentence_rewrite" &&
						(s.original_sentence || s.source_sentence) &&
						s.rewritten_sentence
					) {
						lines.push("   Mode: sentence rewrite");
						lines.push(`   Before: ${s.original_sentence || s.source_sentence}`);
						lines.push(
							`   After: ${emphasizeAnchor(s.rewritten_sentence, anchor)}`,
						);
					} else if (snippet) {
						lines.push(`   Snippet: …${emphasizeAnchor(snippet, anchor)}…`);
					} else {
						lines.push("   Snippet: Preview unavailable");
					}
				});
				after = lines.join("\n");
			} else {
				const sourceLabel =
					issue.issue_type === "low_internal_links" ? "source pages" : "source blogs";
				after =
					`Generate a fix, select one or more ${sourceLabel}, and edit the anchor text before applying`;
			}
			return { before: stringifyValue(before), after: stringifyValue(after) };
		}
		case "low_readability": {
			const d = details as Record<string, unknown>;
			// editedValue only carries rewritten_body, so the score/grade/block
			// numbers come from the stored fix (falling back to issue_details).
			const stored = readabilityFrom(approved);
			const edited = readabilityFrom(editedValue);
			const editedSentenceCount = edited?.sentence_count ?? 0;
			const hasEdit = Boolean(edited?.rewritten_body) || editedSentenceCount > 0;
			const scoreBefore =
				stored?.readability_score_before ??
				(typeof d.readability_score === "number" ? (d.readability_score as number) : undefined);
			const scoreAfter = stored?.readability_score_after;
			const gradeBefore =
				stored?.grade_before ||
				(typeof d.grade_level === "string" ? (d.grade_level as string) : undefined);
			const gradeAfter = stored?.grade_after;
			const blocksRewritten = stored?.blocks_rewritten;
			const before = `Readability ${scoreBefore ?? "?"} (${gradeBefore || "current"})`;
			const hasSuggestion = hasEdit || Boolean(stored?.rewritten_body);
			const simplifiedNote =
				editedSentenceCount > 0
					? ` — ${editedSentenceCount} sentence${editedSentenceCount === 1 ? "" : "s"} simplified`
					: typeof blocksRewritten === "number"
						? ` — ${blocksRewritten} paragraph${blocksRewritten === 1 ? "" : "s"} simplified`
						: "";
			const after = hasSuggestion
				? `Readability ${scoreAfter ?? "?"} (${gradeAfter || "improved"})${simplifiedNote}`
				: "Generate a rewrite, review and edit it before applying";
			return { before: stringifyValue(before), after: stringifyValue(after) };
		}
		case "broken_external": {
			// Dead outbound link: before = the dead target; after = removed
			// (anchor text kept) or repointed to an approved replacement URL.
			const target = sanitizeBrokenUrl(
				(details.target_url as string) ||
					(details.broken_url as string) ||
					(details.url as string) ||
					"",
			);
			const anchor =
				(details.link_text as string) || target || "external link";
			const status =
				(details.verified_status_code as number | undefined) ??
				(details.status_code as number | undefined) ??
				(issue.status_code ?? undefined);
			const repointTo =
				destinationFrom(editedValue) ?? destinationFrom(approved) ?? null;
			const before = `${anchor} → ${target || "(unknown URL)"}${
				status ? ` (HTTP ${status}, dead)` : " (dead link)"
			}`;

			// Resolve the selected fix action so the review reflects what will
			// actually be written — an AI section rewrite shows the rewritten copy,
			// not the generic "link removed" summary.
			const parseFix = (v: unknown): Record<string, unknown> | null => {
				if (!v) return null;
				if (typeof v === "object") return v as Record<string, unknown>;
				if (typeof v === "string") {
					try {
						return JSON.parse(v) as Record<string, unknown>;
					} catch {
						return null;
					}
				}
				return null;
			};
			const fixObj = parseFix(editedValue) ?? parseFix(approved);
			const fixAction = fixObj?.action as string | undefined;
			const stripHtml = (html: string) =>
				html
					.replace(/<[^>]+>/g, "")
					.replace(/\s+/g, " ")
					.trim();

			if (fixAction === "rewrite_section") {
				const sectionAfter = (fixObj?.section_after as string) || "";
				const sectionBefore = (fixObj?.section_before as string) || "";
				const scopeLabel =
					{
						remove_link: "link removed",
						sentence: "sentence rewritten",
						paragraph: "paragraph rewritten",
						section: "section rewritten",
					}[(fixObj?.scope as string) || "sentence"] || "section rewritten";
				return {
					before: stringifyValue(
						sectionBefore ? stripHtml(sectionBefore) : before,
					),
					after: stringifyValue(
						`${stripHtml(sectionAfter)}\n\n(AI rewrite — ${scopeLabel}, dead link removed)`,
					),
				};
			}

			const newText =
				(fixObj?.new_text as string | undefined)?.trim() || null;
			const after =
				fixAction === "delete"
					? `${anchor} — link and its text deleted`
					: repointTo
						? `${newText || anchor} → ${repointTo} (repointed to a working URL${
								newText ? `, relabeled to “${newText}”` : ""
							})`
						: newText
							? `“${newText}” (link removed, anchor text relabeled)`
							: `${anchor} (link removed — anchor text kept)`;
			return { before: stringifyValue(before), after: stringifyValue(after) };
		}
		case "noindex_important": {
			// Deterministic robots-meta flip. Prefer the preview endpoint's
			// apply_preview strings when fetched; otherwise derive the "before" from
			// the crawl's indexability_status detail (falling back to "noindex").
			const d = details as Record<string, unknown>;
			const applyPreview =
				planned.apply_preview &&
				typeof planned.apply_preview === "object" &&
				!Array.isArray(planned.apply_preview)
					? (planned.apply_preview as { before?: unknown; after?: unknown })
					: null;
			const status =
				typeof d.indexability_status === "string" && d.indexability_status.trim()
					? d.indexability_status.trim()
					: "noindex";
			const before =
				typeof applyPreview?.before === "string" && applyPreview.before
					? applyPreview.before
					: `meta robots: ${status}`;
			const after =
				typeof applyPreview?.after === "string" && applyPreview.after
					? applyPreview.after
					: "meta robots: index";
			return { before: stringifyValue(before), after: stringifyValue(after) };
		}
		case "redirect":
		case "broken_internal":
		case "links_to_redirect":
		case "indexability":
		case "sitemap_error": {
			const dest =
				destinationFrom(editedValue) ??
				destinationFrom(approved) ??
				(details.final_url as string) ??
				(planned.recommendation as string);
			return {
				before: stringifyValue(sourceFrom(issue)),
				after: stringifyValue(dest || "Set a destination URL before applying"),
			};
		}
		case "hreflang_error": {
			// Deterministic staged tag-set rewrite. With a fetched preview the
			// planned_payload carries the exact before/after entries; render each as
			// the <link rel="alternate"> tag it is in the page head. mode "suppress"
			// (or an empty after set) means every hreflang tag is removed.
			const d = details as Record<string, unknown>;
			const plan = readHreflangPreview(planned);
			// CLUSTER (two-page) fix: before/after must cover BOTH pages — render one
			// labeled block per page ("Page N — url (role)") with its tag set, so the
			// preview never reads as a single-page rewrite.
			if (plan?.cluster && (plan.pages?.length ?? 0) >= 2) {
				const pages = plan.pages ?? [];
				const pageBlock = (
					url: string,
					idx: number,
					entries: { hreflang: string; href: string }[],
				): string => {
					const role = idx === 0 ? "the page you selected" : "partner page";
					const tags =
						entries.length > 0
							? entries.map(formatHreflangLinkTag).join("\n")
							: NO_HREFLANG_TAGS;
					return `Page ${idx + 1} — ${url} (${role})\n${tags}`;
				};
				return {
					before: stringifyValue(
						pages
							.map((p, idx) => pageBlock(p.url, idx, p.before_entries))
							.join("\n\n"),
					),
					after: stringifyValue(
						pages
							.map((p, idx) => pageBlock(p.url, idx, p.after_entries))
							.join("\n\n"),
					),
				};
			}
			if (plan) {
				const before =
					plan.before_entries.length > 0
						? plan.before_entries.map(formatHreflangLinkTag).join("\n")
						: NO_HREFLANG_TAGS;
				// The "(no hreflang tags)" marker is a suppress-plan claim: only show
				// it when the plan actually suppresses the whole set. A replace plan
				// keeps the valid tags, so render whatever it leaves behind (and only
				// fall back to the marker if that genuinely leaves nothing).
				const after =
					plan.mode === "suppress"
						? NO_HREFLANG_TAGS
						: plan.after_entries.length > 0
							? plan.after_entries.map(formatHreflangLinkTag).join("\n")
							: NO_HREFLANG_TAGS;
				return { before: stringifyValue(before), after: stringifyValue(after) };
			}
			// No fetched plan (verify modal, or a recommendation-only/legacy payload):
			// show the flagged entry from the crawl evidence and point at the preview.
			const lang =
				typeof d.hreflang_language === "string" && d.hreflang_language
					? d.hreflang_language
					: "?";
			const href =
				typeof d.hreflang_url === "string" && d.hreflang_url
					? d.hreflang_url
					: issue.page_url || "";
			const reason =
				typeof d.reason === "string" && d.reason ? ` — ${d.reason}` : "";
			return {
				before: stringifyValue(
					`${formatHreflangLinkTag({ hreflang: lang, href })}${reason}`,
				),
				after: stringifyValue(
					planned.recommendation ||
						"Preview the fix to see the corrected hreflang tag set.",
				),
			};
		}
		case "thin_content": {
			// Phase 1 blog-only additive apply: the reviewed sections/draft
			// additions are APPENDED to the end of the post body — the existing
			// content is never overwritten. Read the edited packet (falling back to
			// the stored fix) so the preview reflects the specialist's edits.
			// orphanFixObject is a generic "parse a fix object or JSON string" helper.
			const packet = orphanFixObject(editedValue) ?? orphanFixObject(approved);
			const d = details as Record<string, unknown>;
			const currentWords = Number(d.word_count ?? 0);
			const sections = Array.isArray(packet?.recommended_sections)
				? (packet?.recommended_sections as Array<Record<string, unknown>>)
				: [];
			const additions = Array.isArray(packet?.draft_additions)
				? (packet?.draft_additions as unknown[]).filter(
						(a): a is string => typeof a === "string" && a.trim().length > 0,
					)
				: [];
			// Backend only appends a section that has a non-empty draft (a bare
			// heading is skipped — no dangling empty <h2>). Mirror that: a
			// heading-only section must NOT appear in the appended "After" list.
			const appendedSections = sections.filter(
				(s) => typeof s.draft === "string" && s.draft.trim().length > 0,
			);
			const headingLines = appendedSections
				.map((s) =>
					typeof s.heading === "string" && s.heading.trim()
						? s.heading.trim()
						: "Suggested content addition",
				)
				.concat(additions.map(() => "Standalone addition"))
				.map((label, i) => `${i + 1}. ${label}`);
			// Project added words from the appended (draft-bearing) sections + additions.
			const addedText = [
				...appendedSections.map((s) => (typeof s.draft === "string" ? s.draft : "")),
				...additions,
			].join(" ");
			const addedWords = addedText.trim() ? addedText.trim().split(/\s+/).length : 0;
			const before = `Existing post body is preserved (nothing is overwritten).\nCurrent: ${currentWords} words`;
			const after =
				headingLines.length > 0
					? `Appended sections (added to the end of the post):\n${headingLines.join(
							"\n",
						)}\n\nProjected: ${currentWords + addedWords} words (+${addedWords})`
					: "Nothing to apply yet — add draft copy to at least one section.";
			return { before: stringifyValue(before), after: stringifyValue(after) };
		}
		default:
			return {
				before: stringifyValue({
					page: issue.page_url,
					evidence: details,
				}),
				after: stringifyValue(planned.recommendation || planned.expected_output || "Recommendation will be recorded."),
			};
	}
}

/**
 * Verify-modal-friendly wrapper around {@link buildBeforeAfter}. The verify
 * result has no fetched fix preview (no planned_payload), so the diff is derived
 * purely from the stored issue + the approved/proposed fix value.
 */
export function buildIssueBeforeAfter(
	issue: PageIssue,
	editedValue?: ProposedFix,
): { before: string; after: string } {
	return buildBeforeAfter(issue, null, editedValue);
}
